import { useRef } from "react";
import { Form, Formik } from "formik";
import { UploadIcon } from "vega-icons";
import * as Yup from "yup";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { FormLabel } from "@vega-core/components/Form/Layout/FormLabel";
import { Input } from "@vega-core/components/shadcn/input";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { NDA_ACCEPT_MIME_TYPES } from "../../constants/ndaConstants";
import { useCoInvestDealReadOnly } from "../../hooks/useCoInvestDealReadOnly";
import { useLpNdaUpload } from "../../hooks/useLpNdaUpload";
import { LpSelector } from "../LpSelector/LpSelector";

type LpNdaUploadSectionProps = {
  dealId: string;
};

type FormValues = {
  selectedLp: string;
  selectedFile: File | null;
};

const initialValues: FormValues = {
  selectedLp: "",
  selectedFile: null,
};

export const LpNdaUploadSection = ({ dealId }: LpNdaUploadSectionProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isMutationLocked } = useCoInvestDealReadOnly();
  const { handleUpload, isUploading } = useLpNdaUpload({
    dealId,
  });

  const validationSchema = Yup.object({
    selectedLp: Yup.string().required(
      m.formValidation_required({
        name: m.coInvestments_lp(),
      }),
    ),
    selectedFile: Yup.mixed().required(
      m.formValidation_required({
        name: m.coInvestments_dealReview_nda(),
      }),
    ),
  });

  const handleFormSubmit = async (values: FormValues) => {
    if (values.selectedLp && values.selectedFile) {
      handleUpload(values.selectedLp, values.selectedFile);
    }
  };

  // Closed deals lock NDA uploads
  if (isMutationLocked) return null;

  return (
    <div className="bg-background/primary border border-border rounded-md p-6 mb-6">
      <h2 className="heading-5 text-text/primary mb-6 sr-only">
        {m.coInvestments_lpSpecificNdas()}
      </h2>

      <Formik
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        validationSchema={validationSchema}
      >
        {({ setFieldValue, isValid, dirty }) => (
          <Form>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <LpSelector
                  dealId={dealId}
                  disabled={isUploading}
                  label={m.coInvestments_lp()}
                  name="selectedLp"
                  placeholder={m.coInvestments_selectLp()}
                />
              </div>

              <div className="space-y-1">
                <FormLabel
                  forName="nda-file-input"
                  label={m.coInvestments_ndaFile_pdfOrWord()}
                />
                <Input
                  accept={NDA_ACCEPT_MIME_TYPES}
                  className={cnTwMerge(
                    "cursor-pointer file:text-foreground px-2 text-base  file:inline-flex file:h-5 file:border-0 file:bg-transparent file:text-sm file:font-medium md:text-sm",
                  )}
                  disabled={isUploading}
                  id="nda-file-input"
                  onChange={(event) => {
                    const [file] = event.target.files ?? [];
                    setFieldValue("selectedFile", file);
                  }}
                  ref={fileInputRef}
                  type="file"
                />
              </div>

              <div className="space-y-1 flex items-end">
                <ThemedButton
                  className="text-sm"
                  dataTestId="upload-lp-nda-button"
                  disabled={!isValid || !dirty || isUploading}
                  icon={<UploadIcon className="w-4 h-4" />}
                  type="submit"
                  variant="secondary"
                >
                  {m.common_upload()}
                </ThemedButton>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
